"use client";

import { useCanAnimate } from "@/hooks/useCanAnimate";

// =============================================================================
// PULSE DOT
// =============================================================================
// Reusable pulsing status indicator. Extracted from cockpit Footer.
// Pulses opacity + glow on a slow loop for a subtle "alive" signal.

interface PulseDotProps {
  /** Dot diameter in px (default: 4) */
  size?: number;
  /** Base color as CSS value (default: foreground at 0.2) */
  color?: string;
  /** Glow color for pulse peak (default: foreground at 0.3) */
  glowColor?: string;
  /** Animation duration in seconds (default: 3.5) */
  duration?: number;
  /** Extra className */
  className?: string;
}

export function PulseDot({
  size = 4,
  color,
  glowColor,
  duration = 3.5,
  className = "",
}: PulseDotProps) {
  const canAnimate = useCanAnimate();

  const bg = color ?? "rgba(var(--foreground-rgb), 0.2)";
  const glow = glowColor ?? "rgba(var(--foreground-rgb), 0.3)";

  return (
    <span
      className={`pulse-dot-indicator ${className}`}
      aria-hidden="true"
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        width: size,
        height: size,
        flexShrink: 0,
      }}
    >
      {/* Expanding ring */}
      {canAnimate && (
        <span
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            border: `1px solid ${bg}`,
            animation: `pulse-ring ${duration}s ease-out infinite`,
          }}
        />
      )}
      {/* Solid dot */}
      <span
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          background: bg,
          animation: canAnimate
            ? `pulse-dot-anim ${duration}s ease-in-out infinite`
            : "none",
        }}
      />
      <style jsx>{`
        @keyframes pulse-dot-anim {
          0%,
          100% {
            opacity: 0.4;
            box-shadow: none;
          }
          50% {
            opacity: 1;
            box-shadow: 0 0 6px ${glow};
          }
        }
        @keyframes pulse-ring {
          0% {
            transform: scale(1);
            opacity: 0.6;
          }
          70% {
            transform: scale(2.8);
            opacity: 0;
          }
          100% {
            transform: scale(2.8);
            opacity: 0;
          }
        }
      `}</style>
    </span>
  );
}
